import { createContext, createEffect, useContext } from 'solid-js';
import { ViewContext } from './ViewContext';
import { RevResponse, view as api } from 'st-app/api';
import { ActionArea } from './fragments/ActionArea';
import { SetStoreFunction, StoreSetter, createStore } from 'solid-js/store';
import { prefersDarkMode, theme, uuidv4 } from 'common';
import { useFocusContext } from '../st-focus/st-focus';
const ST = (window as any).ST || {};

export { ViewContext };
export const ViewContextJSX = createContext<ViewContext>();

export function requireViewContext(): ViewContext {
    const res = useContext(ViewContextJSX);
    if (res) return res;

    throw new Error("Missing view context.");
}

export function useViewContext(): ViewContext | undefined {
    return useContext(ViewContextJSX);
}




type ViewPrefs = Record<string, string>;
const STORAGE_KEY = "st-view.view-prefs";
const cache = new Map<string, [ViewPrefs, SetStoreFunction<ViewPrefs>]>();

export function useViewPreferences(viewGU: string): [ViewPrefs, SetStoreFunction<ViewPrefs>] {
    // return cached store if it exists
    const existing = cache.get(viewGU);
    if (existing) return existing;

    const key = `${STORAGE_KEY}.${viewGU}`;

    // hydrate initial state
    const saved = localStorage.getItem(key);
    const initial: ViewPrefs = saved ? JSON.parse(saved) : {};

    // create the store
    const [viewPrefs, setViewPrefs] = createStore<ViewPrefs>(initial);
    api.GetViewPrefs(viewGU).then(setViewPrefs);

    // wrapper that calls the real setter, then persists & hits your API
    function setPrefs() {
        (setViewPrefs as any).apply(undefined, arguments);

        localStorage.setItem(key, JSON.stringify(viewPrefs));
        api.SetViewPrefs(viewGU, viewPrefs);
    }

    const tuple: [ViewPrefs, SetStoreFunction<ViewPrefs>] = [
        viewPrefs,
        setPrefs,
    ];
    cache.set(viewGU, tuple);

    return tuple;
}

export class STViewProps {
    viewId?: string = undefined;
    tabId?: string = undefined;
    identity?: string = undefined;
}

export default function STView(params: STViewProps) {
    const viewContext = new ViewContext();

    createEffect(() => {
        viewContext.setViewId(params.viewId || '');
        viewContext.setTabId(params.tabId || '');
    });

    return <ViewContextJSX.Provider value={viewContext}>
        <STViewInternal />
        Test: I am a view (viewId: {params.viewId}, identity: {params.identity})
    </ViewContextJSX.Provider>;
}

function STViewInternal() {
    const viewContext = requireViewContext();

    return (
        <div>
            <h1>
                {viewContext.definition?.title}
            </h1>
            <ActionArea />
            {/* Test: I am a view (viewId: {params.viewId}, identity: {params.identity}) */}
        </div>
    );
}

STView.defaultProps = new STViewProps();

async function init(viewId : string) {
    const res = await api.GetViewDef(viewId);
    const viewDefinition = await api.LoadViewDefinition(viewId);

    console.log({ viewDefinition });

    if (ST.DEBUGGING_COMPONENT) {
        console.log(res);
    }
}

type ViewType = 'VIEW'
type ViewMode = 'NORMAL' | 'ADD' | 'DETAIL' | 'FIND_RESULT' | 'FK_SELECT' | 'EXT_OBJ_SELECT' | 'DIALOG' | 'CHOOSER' | 'JOB_Q_EXECUTE' | 'QUERY';

export function loadView(
    type: ViewType,
    id: string,
    detached = false,
    noRemember = false,
    includeState = false,
    includeViewNode = false,
    focusKey: string | undefined = undefined,
    extendData: Record<string, any> | undefined = undefined,
) {
    if (ST.LoadView) {
        ST.LoadView(type, id, detached, noRemember, includeState, includeViewNode, focusKey, extendData);
        return
    }

    if (detached) {
        window.open(`/views/${id}`, undefined, 'popup=true');
    }
}

export function openViewWindow(
    viewGU: string,
    tabGU: string | undefined = undefined,
    identity: string | undefined = undefined,
    extraParams: Record<string, any> | undefined = undefined,
) {
    if (ST.OpenViewWindow) {
        ST.OpenViewWindow(viewGU, tabGU, identity, extraParams);
        return;
    }

    alert(`Open view ${viewGU} in window`);
}


export async function openViewInDialog(viewID: string, identity: string | undefined = undefined, options?: OpenViewOptions): Promise<void> {
    if (ST.OpenViewInDialog) {
        const extra = {
            REV_RENDER: {
                VIEW: {
                    '@MODE': options?.mode ?? 'NORMAL',
                }
            }
        };

        return ST.OpenViewInDialog(viewID, identity, extra);
    }

    alert(`Open view ${viewID} in dialog`);
}


interface OpenViewOptions {
    mode?: ViewMode
}

type ResponseActionOptions = {
    // return true if the request was handled
    handleXml?: (xml: HTMLElement) => boolean;
};

export function processResponseActions(res: RevResponse, opts?: ResponseActionOptions) {
    if (res.REV_DATA_ROOT?.clientActions) {
        for (const action of res.REV_DATA_ROOT.clientActions) {
            const xml = (action as any).xml;
            if (xml) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xml, "application/xml");

                const executeClientAction = (window.top ?? window)?.ST?.ExecuteClientAction;

                if (opts?.handleXml?.call(window, xmlDoc.documentElement)) {
                    return;
                }

                if (executeClientAction) {
                    executeClientAction.call(null, xmlDoc.documentElement)
                }
                else {
                    console.log(xmlDoc.documentElement);
                }
            }
            else {
                switch (action.actionType) {
                }
            }
        }
    }
}

// Simple JSON to XML converter for REV_REQUEST structures
function jsonToXml(obj: any): string {
    if (typeof obj === 'string') {
        // Don't escape - pass through as-is for CDATA content
        return obj;
    }
    if (typeof obj === 'number' || typeof obj === 'boolean') {
        return String(obj);
    }
    if (obj === null || obj === undefined) {
        return '';
    }
    if (Array.isArray(obj)) {
        return obj.map(item => jsonToXml(item)).join('');
    }

    let xml = '';
    for (const key in obj) {
        if (!key.startsWith('@')) {
            const value = obj[key];

            // Handle arrays (like D: [{@K: 'x', @V: 'y'}, ...])
            if (Array.isArray(value)) {
                value.forEach(item => {
                    const attributes: string[] = [];
                    const children: string[] = [];

                    if (typeof item === 'object' && !Array.isArray(item)) {
                        for (const attrKey in item) {
                            if (attrKey.startsWith('@')) {
                                const attrName = attrKey.substring(1);
                                const attrValue = item[attrKey];
                                if (attrValue !== undefined && attrValue !== null && attrValue !== '') {
                                    attributes.push(`${attrName}="${String(attrValue)}"`);
                                }
                            } else {
                                children.push(jsonToXml({ [attrKey]: item[attrKey] }));
                            }
                        }
                    }

                    const attrString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';
                    if (children.length > 0) {
                        xml += `<${key}${attrString}>${children.join('')}</${key}>`;
                    } else {
                        xml += `<${key}${attrString}/>`;
                    }
                });
                continue;
            }

            const attributes: string[] = [];

            if (typeof value === 'object' && value !== null) {
                for (const attrKey in value) {
                    if (attrKey.startsWith('@')) {
                        const attrName = attrKey.substring(1);
                        const attrValue = value[attrKey];
                        if (attrValue !== undefined && attrValue !== null && attrValue !== '') {
                            attributes.push(`${attrName}="${String(attrValue)}"`);
                        }
                    }
                }
            }

            const attrString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';

            const children: string[] = [];
            let hasChildren = false;
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                for (const childKey in value) {
                    if (!childKey.startsWith('@')) {
                        hasChildren = true;
                        children.push(jsonToXml({ [childKey]: value[childKey] }));
                    }
                }
            }

            if (hasChildren) {
                xml += `<${key}${attrString}>${children.join('')}</${key}>`;
            } else if (typeof value === 'object' && (Object.keys(value).length === 0 || Object.keys(value).every(k => k.startsWith('@')))) {
                xml += `<${key}${attrString}/>`;
            } else if (typeof value === 'object') {
                xml += `<${key}${attrString}>${jsonToXml(value)}</${key}>`;
            } else {
                xml += `<${key}${attrString}>${jsonToXml(value)}</${key}>`;
            }
        }
    }
    return xml;
}

export function openInIFrame(xmlOrJson: string | object, iframe: HTMLIFrameElement) {
    let xml: string;

    // Convert JSON to XML first if needed
    if (typeof xmlOrJson === 'object') {
        xml = jsonToXml(xmlOrJson);
    } else {
        xml = xmlOrJson;
    }

    // Only wrap if not already wrapped in REV_REQUEST
    if (xml && typeof xml === 'string') {
        const trimmed = xml.trim();
        if (!trimmed.startsWith('<REV_REQUEST>') && !trimmed.startsWith('<?xml')) {
            xml = `<REV_REQUEST><EVENT NAME="WDWActionEvent"><REQUEST>${xml}</REQUEST></EVENT></REV_REQUEST>`;
        }
    }

    let form = document.querySelector('body > form.iframe-target') as HTMLFormElement;
    let input = form?.querySelector('input[name="data"]') as HTMLInputElement;
    let focus = form?.querySelector('input[name="FOCUS_KEY"]') as HTMLInputElement;

    if (!form) {
        form = document.createElement('form');
        form.setAttribute('enctype', 'application/x-www-form-urlencoded');
        form.setAttribute('method', 'post');
        form.classList.add('iframe-target');
        document.body.prepend(form);

        input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', 'data');
        input.name = 'data';
        form.appendChild(input);

        focus = document.createElement('input');
        focus.setAttribute('type', 'hidden');
        focus.setAttribute('name', 'FOCUS_KEY');
        focus.name = 'FOCUS_KEY';
        form.appendChild(focus);
    }

    if (!iframe.id) {
        iframe.id = 'iframe_' + uuidv4().replaceAll('-', '');
        iframe.name = iframe.id;
        iframe.setAttribute('name', iframe.id);
    }

    const params = [];
    params.push('RAW=Y');
    if (ST.Impersonated) {
        params.push('IMPERSONATE=Y');
    }
    const qstr = params.length ? '?' + params.join('&') : '';

    const iframeWnd = iframe?.contentWindow;
    if (iframeWnd) {
        iframe.setAttribute('src', getLoaderURL());

        form.setAttribute('action', 'ST_Content.aspx' + qstr);
        form.setAttribute('target', iframe.id);

        // browsers don't always register the change in target, so we
        // need to force the browser to reload the form details
        document.body.removeChild(form);
        document.body.prepend(form);

        return onWindowLoaded(iframeWnd)
            .then(() => {
                iframe.focus();
                input.value = xml;

                const focusCtx = useFocusContext();
                if (focusCtx && focus) {
                    focus.value = focusCtx.focusKey();
                }

                form.submit();

                return onWindowLoaded(iframeWnd);
            });
    }
}

function onWindowLoaded(wnd: Window, timeout?: number) {
    return new Promise<Window | undefined>((resolve, reject) => {
        let onLoaded: NodeJS.Timeout;
        var tookTooLong = setTimeout(function () {
            clearInterval(onLoaded);
            resolve(undefined);
        }, timeout || 15000);

        onLoaded = setInterval(function () {
            if ((wnd as any).st_loaded == true) {
                clearTimeout(tookTooLong);
                clearInterval(onLoaded);
                resolve(wnd);
            }
        }, 200);
    });
}

function getLoaderURL() {
    if (theme() === 'default') {
        if (prefersDarkMode()) {
            return 'ST_Loading.html?theme=1';
        }
        else {
            return 'ST_Loading.html';
        }
    }
    else {
        return 'ST_Loading.html?theme=' + theme();
    }
}